import { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppBar, Box, Toolbar } from '@mui/material';
import clsx from 'clsx';
import * as R from 'ramda';

import { triggerLogout } from '@atom/actions/userActions';
import Logo from '@atom/components/common/navigation/Logo';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Button, Progress, TextField } from '@atom/mui';
import userSelectors from '@atom/selectors/userSelectors';
import colors from '@atom/styles/colors';
import layoutStyles from '@atom/styles/layout';
import { RootState } from '@atom/types/store';
import accountUtilities from '@atom/utilities/accountUtilities';

import LoginFooter from './LoginFooter';

import classes from './login.module.css';

const navBarStyle = {
  height: layoutStyles.topNavHeight,
  backgroundColor: colors.neutral.white,
  boxShadow: '0 2px 4px 1px',
  color: colors.utility.boxShadow,
  position: 'fixed',
  top: 0,
};

const DEV_TENANT_NAME_VALIDATION = new RegExp('^dev\\-[-a-zA-Z]+$');
const QA_TENANT_NAME_VALIDATION = new RegExp('^qa\\-[-a-zA-Z]+$');
const UAT_TENANT_NAME_VALIDATION = new RegExp('^uat\\-[-a-zA-Z]+$');
const DEFAULT_TENANT_NAME_VALIDATION = new RegExp('^[a-zA-Z][-a-zA-Z]+$');

const TenantEntry = () => {
  const [, { Link, backDestination, navigate }] = useNavigation();
  const [tenant, setTenant] = useState<string>('');
  const [error, setError] = useState<boolean>(false);

  const dispatch = useDispatch();
  const loading = useSelector(
    (rootState: RootState) => rootState.loading.loadingTenantCreation,
  );

  const email = accountUtilities.getLoggedInEmail();

  useEffect(() => {
    if (!accountUtilities.isEmailVerified()) {
      navigate('/verify-email');
    }
  }, []);

  if (!email) {
    navigate('/login');
  }

  const navigateBack = () => {
    navigate(backDestination('/login'));
  };

  const isValidTenantName = (host: string): boolean => {
    if (host === 'dev-atom' || host === 'local-atom') {
      return DEV_TENANT_NAME_VALIDATION.test(tenant);
    }

    if (host === 'qa-atom') {
      return QA_TENANT_NAME_VALIDATION.test(tenant);
    }

    if (host === 'uat-atom') {
      return UAT_TENANT_NAME_VALIDATION.test(tenant);
    }

    if (
      host === 'atomapp' &&
      (DEV_TENANT_NAME_VALIDATION.test(tenant) ||
        QA_TENANT_NAME_VALIDATION.test(tenant) ||
        UAT_TENANT_NAME_VALIDATION.test(tenant))
    ) {
      return false;
    } else if (host === 'atomapp') {
      return DEFAULT_TENANT_NAME_VALIDATION.test(tenant);
    }

    return false;
  };

  const getCurrentHost = (): string => {
    return window.location.hostname.split('.')[2]
      ? R.pipe(
          R.pathOr('www.atomapp.com', ['location', 'hostname']),
          R.split('.'),
          R.drop(1),
          R.join('.'),
        )(window)
      : R.pipe(R.pathOr('www.atomapp.com', ['location', 'hostname']))(window);
  };

  const submit = () => {
    const host = window.location.hostname.split('.')[2]
      ? window.location.hostname.split('.')[1]
      : window.location.hostname.split('.')[0];

    if (isValidTenantName(host)) {
      setError(false);
      const protocol = window.location.protocol;
      const token = accountUtilities.getToken();
      const domain = userSelectors.getDomain(tenant);

      window.location.href = `${protocol}//${domain}/callback/${token}?tenant=${tenant}`;
    } else {
      setError(true);
    }
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setTenant(value);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      submit();
    }
  };

  const getErrorText = () => {
    if (error) {
      return (
        <div className={classes.tenantCreationErrorText}>
          Please enter a valid tenant name. The name cannot include any numbers
          or special characters.
        </div>
      );
    }

    return <div />;
  };

  const logout = () => {
    dispatch(triggerLogout());
  };

  const content = loading ? (
    <div className={classes.spinnerContainer}>
      <Progress />
    </div>
  ) : (
    <>
      {getErrorText()}
      <div className={clsx(classes.textFieldContainer, classes.entry)}>
        Enter the workspaces URL. If you do not know the workspaces URL, please
        contact the team manager.
      </div>
      <div className={classes.tenantCreationInputContainer}>
        <TextField
          variant="standard"
          name="tenant"
          value={tenant}
          onChange={onChange}
          onKeyDown={onKeyDown}
        />
        <div
          className={classes.tenantCreationInputLabel}
        >{`.${getCurrentHost()}`}</div>
      </div>
      <div className={classes.tenantCreationButtonContainer}>
        <Button color="primary" variant="contained" onClick={submit}>
          Go to Atom
        </Button>
      </div>
      <div className={classes.tenantSelectionContainer}>
        <div className={classes.tenantDecisionContainer}>
          <div className={classes.tenantDecisionContainer}>
            <div className={classes.tenantSelectionLink} onClick={navigateBack}>
              Go back
            </div>
            or
            <Link to="/tenant-creation">
              <div className={classes.tenantSelectionLink}>
                Create a workspace
              </div>
            </Link>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <AppBar style={navBarStyle}>
        <Toolbar>
          <div>
            <Logo />
          </div>
          <Box sx={{ flexGrow: 1 }} />
          <div className={classes.navRightContainer}>
            <div className={classes.navRightText}>{email}</div>
            <div className={classes.navRightLogin} onClick={logout}>
              Sign Out
            </div>
          </div>
        </Toolbar>
      </AppBar>
      <div className={classes.container}>
        <div className={clsx(classes.authBackground, classes.complete)} />
        <div className={classes.headingText}>Join a workspace</div>

        {content}
        <LoginFooter />
      </div>
    </>
  );
};

export default TenantEntry;
